import {
  normalizeStoryCompanionId,
  normalizeStoryCompanionRoster,
} from "./storyCompanions";
import {
  normalizeStorySideQuestProgressMap,
  normalizeTurnedInStorySideQuestIds,
} from "./storySideQuests";
import {
  normalizeStoryTeamMemberKey,
  normalizeUnlockedStoryTeamRoster,
  resolveActiveStoryTeamRoster,
} from "./storyTeamRules";

const DEFAULT_STORY_STATE = Object.freeze({
  infinityTokens: 0,
  level: 1,
  xp: 0,
  pendingBoostAllocations: 0,
  storyCharacterBoosts: {},
  storyXpClaims: {},
  storyBattleXpClaims: {},
  storyBattleResolutionClaims: {},
  inventoryCount: 0,
  packUnlocked: false,
  storyTeamUnlocked: false,
  storyTeamRoster: ["zay"],
  activeStoryTeamRoster: ["zay"],
  activeStoryAvatarKey: "zay",
  storyCompanionUnlocked: false,
  storyCompanionRoster: [],
  activeStoryCompanionId: null,
  storyMissionEncounterProgress: {},
  activeStorySideQuestId: null,
  storySideQuestProgress: {},
  turnedInStorySideQuestIds: [],
  voidCaveUnlocked: false,
  runeStoneTravelUnlocked: false,
  startedAt: null,
  updatedAt: null,
});

const toFiniteNumber = (value, fallback) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
};

const toArray = (value) => (Array.isArray(value) ? value : []);

export const normalizeStoryItemId = (name = "") =>
  String(name)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const normalizeInventoryKind = (kind) =>
  kind === "infinityCard" || kind === "storyItem" ? kind : "storyItem";

const normalizeStoryAvatarKey = normalizeStoryTeamMemberKey;
const normalizeStoryTeamRoster = normalizeUnlockedStoryTeamRoster;

const normalizeEncounterProgressId = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9:-]+/g, "-")
    .replace(/^-+|-+$/g, "");

const normalizeEncounterProgressIdList = (value = []) =>
  [...new Set((Array.isArray(value) ? value : []).map(normalizeEncounterProgressId).filter(Boolean))];

const normalizeStoryMissionEncounterProgressEntry = (entry = {}) => ({
  defeatedEncounterIds: normalizeEncounterProgressIdList(
    entry?.defeatedEncounterIds,
  ),
  defeatedCreatureCount: Math.max(
    0,
    Math.floor(toFiniteNumber(entry?.defeatedCreatureCount, 0)),
  ),
  totalCreatureCount: Math.max(
    0,
    Math.floor(toFiniteNumber(entry?.totalCreatureCount, 0)),
  ),
  revealedItemIds: normalizeEncounterProgressIdList(entry?.revealedItemIds),
  lastTriggeredEncounterId:
    normalizeEncounterProgressId(entry?.lastTriggeredEncounterId) || null,
});

const normalizeStoryMissionEncounterProgress = (rawProgress = {}) =>
  Object.entries(rawProgress || {}).reduce((acc, [rawMissionId, rawEntry]) => {
    const missionId = String(rawMissionId || "").trim();
    if (!missionId) return acc;

    acc[missionId] = normalizeStoryMissionEncounterProgressEntry(rawEntry);
    return acc;
  }, {});

export const STORY_BOOST_STAT_KEYS = Object.freeze([
  "health",
  "attack",
  "defense",
  "accuracy",
]);

export const DEFAULT_STORY_CHARACTER_BOOSTS = Object.freeze({
  health: 0,
  attack: 0,
  defense: 0,
  accuracy: 0,
});

const normalizeStoryLevel = (value) => {
  const rawValue = String(value ?? DEFAULT_STORY_STATE.level).trim();
  if (/^m\d+$/i.test(rawValue)) return rawValue.toUpperCase();

  const numericLevel = Math.max(
    1,
    Math.floor(toFiniteNumber(value, DEFAULT_STORY_STATE.level)),
  );
  return numericLevel;
};

const normalizeBoostMap = (boosts = {}) =>
  STORY_BOOST_STAT_KEYS.reduce((acc, statKey) => {
    acc[statKey] = Math.max(
      0,
      Math.floor(toFiniteNumber(boosts?.[statKey], DEFAULT_STORY_CHARACTER_BOOSTS[statKey])),
    );
    return acc;
  }, {});

export const normalizeStoryCharacterBoosts = (
  rawBoosts = {},
  storyTeamRoster = ["zay"],
) => {
  const rosterKeys = new Set(
    normalizeStoryTeamRoster(storyTeamRoster).map(normalizeStoryAvatarKey),
  );

  return Object.entries(rawBoosts || {}).reduce((acc, [rawKey, rawValue]) => {
    const rosterKey = normalizeStoryAvatarKey(rawKey);
    if (!rosterKey || !rosterKeys.has(rosterKey)) return acc;

    const normalizedBoosts = normalizeBoostMap(rawValue);
    const totalBoosts = Object.values(normalizedBoosts).reduce(
      (total, value) => total + value,
      0,
    );
    if (totalBoosts <= 0) return acc;

    acc[rosterKey] = normalizedBoosts;
    return acc;
  }, {});
};

const normalizeStoryXpClaims = (rawClaims = {}) =>
  Object.entries(rawClaims || {}).reduce((acc, [rawMissionId, rawClaim]) => {
    const missionId = String(rawMissionId || "").trim();
    if (!missionId) return acc;

    const xp = Math.max(0, Math.floor(toFiniteNumber(rawClaim?.xp, 0)));
    if (xp <= 0) return acc;

    acc[missionId] = {
      xp,
      awardedAt: rawClaim?.awardedAt ?? null,
    };
    return acc;
  }, {});

const normalizeStoryBattleXpClaimBreakdown = (rawBreakdown = {}) => ({
  totalXp: Math.max(0, Math.floor(toFiniteNumber(rawBreakdown?.totalXp, 0))),
  rarityXp: Math.max(0, Math.floor(toFiniteNumber(rawBreakdown?.rarityXp, 0))),
  cardPlayXp: Math.max(
    0,
    Math.floor(toFiniteNumber(rawBreakdown?.cardPlayXp, 0)),
  ),
  bossXp: Math.max(0, Math.floor(toFiniteNumber(rawBreakdown?.bossXp, 0))),
  enemyCount: Math.max(0, Math.floor(toFiniteNumber(rawBreakdown?.enemyCount, 0))),
  storyCardsPlayed: Math.max(
    0,
    Math.floor(toFiniteNumber(rawBreakdown?.storyCardsPlayed, 0)),
  ),
  hasBoss: Boolean(rawBreakdown?.hasBoss),
  eligible: Boolean(rawBreakdown?.eligible),
});

const normalizeStoryBattleXpClaims = (rawClaims = {}) =>
  Object.entries(rawClaims || {}).reduce((acc, [rawRoomId, rawClaim]) => {
    const roomId = String(rawRoomId || "").trim();
    if (!roomId) return acc;

    const xp = Math.max(0, Math.floor(toFiniteNumber(rawClaim?.xp, 0)));
    if (xp <= 0) return acc;

    acc[roomId] = {
      xp,
      awardedAt: rawClaim?.awardedAt ?? null,
      missionId: rawClaim?.missionId ?? null,
      encounterId: rawClaim?.encounterId ?? null,
      resultKey: rawClaim?.resultKey ?? null,
      breakdown: normalizeStoryBattleXpClaimBreakdown(rawClaim?.breakdown),
    };
    return acc;
  }, {});

const normalizeStoryBattleTokenReward = (rawReward = {}) => ({
  claimKey: String(rawReward?.claimKey || "").trim() || null,
  missionId: rawReward?.missionId ?? null,
  encounterId: rawReward?.encounterId ?? null,
  totalTokens: Math.max(
    0,
    Math.floor(toFiniteNumber(rawReward?.totalTokens, 0)),
  ),
  rarityTokens: Math.max(
    0,
    Math.floor(toFiniteNumber(rawReward?.rarityTokens, 0)),
  ),
  enemyCount: Math.max(
    0,
    Math.floor(toFiniteNumber(rawReward?.enemyCount, 0)),
  ),
  eligible: Boolean(rawReward?.eligible),
});

const normalizeStoryBattleConsumedCard = (rawCard = {}) => ({
  sourceId: normalizeStoryItemId(rawCard?.sourceId || rawCard?.id || rawCard?.name),
  id: rawCard?.id ?? null,
  name: rawCard?.name ?? null,
  imageUrl: rawCard?.imageUrl || "",
  timesPlayed: Math.max(
    0,
    Math.floor(toFiniteNumber(rawCard?.timesPlayed, 0)),
  ),
  consumedQuantity: Math.max(
    0,
    Math.floor(toFiniteNumber(rawCard?.consumedQuantity, 0)),
  ),
  remainingQuantity: Math.max(
    0,
    Math.floor(toFiniteNumber(rawCard?.remainingQuantity, 0)),
  ),
  fullyConsumable: Boolean(rawCard?.fullyConsumable),
  duplicateConsumable: Boolean(rawCard?.duplicateConsumable),
});

const normalizeStoryBattleResolutionClaims = (rawClaims = {}) =>
  Object.entries(rawClaims || {}).reduce((acc, [rawRoomId, rawClaim]) => {
    const roomId = String(rawRoomId || "").trim();
    if (!roomId) return acc;

    acc[roomId] = {
      missionId: rawClaim?.missionId ?? null,
      encounterId: rawClaim?.encounterId ?? null,
      resultKey: rawClaim?.resultKey ?? null,
      resolvedAt: rawClaim?.resolvedAt ?? null,
      tokenReward: normalizeStoryBattleTokenReward(rawClaim?.tokenReward),
      consumedStoryCards: toArray(rawClaim?.consumedStoryCards)
        .map(normalizeStoryBattleConsumedCard)
        .filter((card) => card.sourceId),
    };
    return acc;
  }, {});

export const getStoryInventoryDocId = (entry = {}) => {
  const normalizedEntry = normalizeStoryInventoryEntry(entry);
  if (!normalizedEntry) return null;

  return `${normalizedEntry.kind}:${normalizedEntry.sourceId}`;
};

export const normalizeStoryInventoryEntry = (entry) => {
  if (!entry) return null;

  const rawName =
    typeof entry === "string"
      ? entry
      : typeof entry?.name === "string"
        ? entry.name
        : typeof entry?.id === "string"
          ? entry.id
          : "";

  const name = rawName.trim();
  const kind = normalizeInventoryKind(entry?.kind);
  const sourceId = normalizeStoryItemId(entry?.sourceId || entry?.id || name);

  if (!name || !sourceId) return null;

  return {
    id: entry?.id || sourceId,
    kind,
    sourceId,
    name,
    quantity: Math.max(0, Math.floor(toFiniteNumber(entry?.quantity ?? 1, 1))),
    imageUrl: entry?.imageUrl || "",
    imageKey: entry?.imageKey || "",
    stackable: entry?.stackable ?? kind === "storyItem",
    acquiredAt: entry?.acquiredAt ?? null,
    updatedAt: entry?.updatedAt ?? null,
  };
};

export const normalizeStoryInventory = (inventory = []) =>
  inventory.reduce((items, entry) => {
    const normalizedEntry = normalizeStoryInventoryEntry(entry);
    if (!normalizedEntry || normalizedEntry.quantity <= 0) return items;

    const existingIndex = items.findIndex(
      (item) =>
        item.kind === normalizedEntry.kind &&
        item.sourceId === normalizedEntry.sourceId,
    );
    if (existingIndex === -1) {
      items.push(normalizedEntry);
      return items;
    }

    items[existingIndex] = {
      ...items[existingIndex],
      name: items[existingIndex].name || normalizedEntry.name,
      quantity: items[existingIndex].quantity + normalizedEntry.quantity,
      imageUrl: items[existingIndex].imageUrl || normalizedEntry.imageUrl,
      imageKey: items[existingIndex].imageKey || normalizedEntry.imageKey,
      updatedAt: normalizedEntry.updatedAt || items[existingIndex].updatedAt,
    };

    return items;
  }, []);

export const resolveStoryState = (rawState = {}) => {
  const level = normalizeStoryLevel(rawState?.level);
  const storyTeamRoster = normalizeStoryTeamRoster(rawState?.storyTeamRoster);
  const activeStoryTeamRoster = resolveActiveStoryTeamRoster({
    storyTeamRoster,
    activeStoryTeamRoster: rawState?.activeStoryTeamRoster,
    storyLevel: level,
  });
  const requestedAvatarKey = normalizeStoryAvatarKey(
    rawState?.activeStoryAvatarKey || DEFAULT_STORY_STATE.activeStoryAvatarKey,
  );
  const activeStoryAvatarKey = activeStoryTeamRoster.includes(requestedAvatarKey)
    ? requestedAvatarKey
    : DEFAULT_STORY_STATE.activeStoryAvatarKey;
  const normalizedCompanionRoster = normalizeStoryCompanionRoster(
    rawState?.storyCompanionRoster,
  );
  const requestedCompanionId = normalizeStoryCompanionId(
    rawState?.activeStoryCompanionId,
  );
  const storyCompanionUnlocked = Boolean(
    rawState?.storyCompanionUnlocked ||
      normalizedCompanionRoster.length > 0 ||
      requestedCompanionId,
  );
  const storyCompanionRoster = storyCompanionUnlocked
    ? normalizedCompanionRoster.length
      ? normalizedCompanionRoster
      : requestedCompanionId
        ? [requestedCompanionId]
        : []
    : [];
  const activeStoryCompanionId =
    storyCompanionUnlocked && storyCompanionRoster.includes(requestedCompanionId)
      ? requestedCompanionId
      : null;
  const activeStorySideQuestId =
    String(rawState?.activeStorySideQuestId || "")
      .trim()
      .toLowerCase() || null;

  return {
    infinityTokens: Math.max(
      0,
      Math.floor(toFiniteNumber(rawState?.infinityTokens, DEFAULT_STORY_STATE.infinityTokens)),
    ),
    level,
    xp: Math.max(0, Math.floor(toFiniteNumber(rawState?.xp, DEFAULT_STORY_STATE.xp))),
    pendingBoostAllocations: Math.max(
      0,
      Math.floor(
        toFiniteNumber(
          rawState?.pendingBoostAllocations,
          DEFAULT_STORY_STATE.pendingBoostAllocations,
        ),
      ),
    ),
    storyCharacterBoosts: normalizeStoryCharacterBoosts(
      rawState?.storyCharacterBoosts,
      storyTeamRoster,
    ),
    storyXpClaims: normalizeStoryXpClaims(rawState?.storyXpClaims),
    storyBattleXpClaims: normalizeStoryBattleXpClaims(rawState?.storyBattleXpClaims),
    storyBattleResolutionClaims: normalizeStoryBattleResolutionClaims(
      rawState?.storyBattleResolutionClaims,
    ),
    inventoryCount: Math.max(
      0,
      Math.floor(
        toFiniteNumber(rawState?.inventoryCount, DEFAULT_STORY_STATE.inventoryCount),
      ),
    ),
    packUnlocked: Boolean(rawState?.packUnlocked),
    storyTeamUnlocked: Boolean(rawState?.storyTeamUnlocked),
    storyTeamRoster,
    activeStoryTeamRoster,
    activeStoryAvatarKey,
    storyCompanionUnlocked,
    storyCompanionRoster,
    activeStoryCompanionId,
    storyMissionEncounterProgress: normalizeStoryMissionEncounterProgress(
      rawState?.storyMissionEncounterProgress,
    ),
    activeStorySideQuestId,
    storySideQuestProgress: normalizeStorySideQuestProgressMap(
      rawState?.storySideQuestProgress,
    ),
    turnedInStorySideQuestIds: normalizeTurnedInStorySideQuestIds(
      rawState?.turnedInStorySideQuestIds,
    ),
    voidCaveUnlocked: Boolean(rawState?.voidCaveUnlocked),
    runeStoneTravelUnlocked: Boolean(rawState?.runeStoneTravelUnlocked),
    startedAt: rawState?.startedAt ?? DEFAULT_STORY_STATE.startedAt,
    updatedAt: rawState?.updatedAt ?? DEFAULT_STORY_STATE.updatedAt,
  };
};

export const buildInitialStoryState = (patch = {}) =>
  resolveStoryState({
    ...DEFAULT_STORY_STATE,
    ...patch,
  });
